import {
  Component,
  OnInit,
  Input,
  forwardRef,
  ViewChild,
  Injector,
  Injectable,
  Output,
  EventEmitter
} from '@angular/core';
import {
  NgbTimeStruct,
  NgbDatepickerConfig,
  NgbDateStruct,
  NgbPopoverConfig,
  NgbPopover,
  NgbDatepicker,
  NgbDatepickerI18n
} from '@ng-bootstrap/ng-bootstrap';
import {NG_VALUE_ACCESSOR, ControlValueAccessor, NgControl} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {DateTimeModel} from './date-time.model';
import {noop} from 'rxjs';
import {Globals} from '../../../globals';

const I18N_VALUES = {
  'ru': {
    weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    months: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
    header: "Выбрать дату и время",
    header_no_time: "Выбрать дату",
    hours: "Часы",
    minutes: "Минуты",
    seconds: "Секунды"
  },
  'en': {
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    header: "Date & Time Select",
    header_no_time: "Date Select",
    hours: "Hours",
    minutes: "Minutes",
    seconds: "Seconds"
  },
  'es': {
    weekdays: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
    months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'],
    header: "Seleccionar fecha y hora",
    header_no_time: "Seleccionar fecha",
    hours: "Horas",
    minutes: "Minutos",
    seconds: "Segundos"
  }
};

@Injectable()
export class I18n {
  language: string;
  changeLangCancel: any;

  constructor(public Globals: Globals) {
    // translate
    this.changeLangCancel = this.Globals.langJS.subscribe(this.onLangChanged.bind(this));
  }
  onLangChanged() {
    this.language = this.Globals.lang;
  }

  ngOnDestroy() { // tslint:disable-line:use-lifecycle-interface
    this.changeLangCancel?.unsubscribe();
  }
}

// Define custom service providing the months and weekdays translations
@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  constructor(private _i18n: I18n) {
    super();
  }
  getWeekdayShortName(weekday: number): string {
    return I18N_VALUES[this._i18n.language].weekdays[weekday - 1];
  }
  getMonthShortName(month: number): string {
    return I18N_VALUES[this._i18n.language].months[month - 1];
  }
  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }
  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}-${date.month}-${date.year}`;
  }
}

@Component({
  selector: 'app-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.scss'],
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true
    },
    I18n,
    {
      provide: NgbDatepickerI18n,
      useClass: CustomDatepickerI18n
    },
    NgbDatepickerConfig

  ]
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit {
  @Input()
  placement = 'auto';
  @Input()
  dateString: string;

  @Output()
  catchInputDate = new EventEmitter<any>();

  @Output()
  catchPick = new EventEmitter<any>();

  @Input()
  inputDatetimeFormat = 'dd.MM.yyyy HH:mm:ss';
  @Input()
  hourStep = 1;
  @Input()
  minuteStep = 1;
  @Input()
  secondStep = 1;
  @Input()
  seconds = true;

  @Input()
  disabled = false;

  public datetime: DateTimeModel = new DateTimeModel({hour: 0, minute: 0, second: 0});
  private firstTimeAssign = true;

  @ViewChild(NgbPopover, {static: false})
  private popover: NgbPopover;

  private onTouched: () => void = noop;
  private onChange: (_: any) => void = noop;

  ngControl: NgControl;

  // получаем проверку из диалога добавления проекта
  @Input()
  project = false;

  //для даты окончания проекта
  @Input()
  project_date_after = false;

  // получаем проверку из диалога добавления юзера
  @Input()
  user = false;

  // получаем идентификатор инпута, в который тыкаем в отчетах
  @Input()
  report_date_type: number;

  min_date = null;

  header_lang: string;
  hours_lang: string;
  minutes_lang: string;
  seconds_lang: string;

  changeLangCancel: any;
  language: string;
  input_change = true;

  // для первичного анализа - не показывать время и выводить другой заголовок
  @Input()
  no_time = false;

  header_no_time_lang: string;

  mask = '00.00.0000 00:00:00';

  constructor(
    private config: NgbPopoverConfig,
    private inj: Injector,
    public configDate: NgbDatepickerConfig,
    public Globals: Globals
  ) {
    config.autoClose = 'outside';
    config.placement = 'auto';
    this.changeLangCancel = this.Globals.langJS.subscribe(() => {
      this.onLangChanged();
    });

  }

  input_change_timeout() {
    this.input_change = false;
    setTimeout(() => {
      this.input_change = true;
    }, 10);
  }

  onLangChanged() {
    this.language = this.Globals.lang;
    this.header_lang = I18N_VALUES[this.language].header;
    this.header_no_time_lang = I18N_VALUES[this.language].header_no_time;
    this.hours_lang = I18N_VALUES[this.language].hours;
    this.minutes_lang = I18N_VALUES[this.language].minutes;
    this.seconds_lang = I18N_VALUES[this.language].seconds;
  }

  openpopup() {
    //устанавливаем минимальную дату, если она где-то  в копонентах была установлена
    if (Boolean(this.Globals.minDate && this.Globals.minDate != null)) {
      this.configDate.minDate = this.Globals.minDate;
    }

    //устанавливаем минимальную дату сегодня для даты окончания проекта в диалоге проекта и дату истечения в диалоге юзера
    if (this.min_date != null) {
      this.configDate.minDate = this.min_date;
    }
  }

  ngOnInit(): void {
    this.ngControl = this.inj.get(NgControl);

    let today = new Date();

    //проверяем, состоялся ли переход со стр. диалога проекта или  с других страниц
    if (!this.project) {
      this.configDate.maxDate = {year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate()};
    } else {
      //устанавливаем минимальную дату сегодня для даты окончания проекта
      if (this.project_date_after) {
        this.min_date = {year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate()}
      }
    }
    //устанавливаем минимальную дату сегодня для даты истечения юзера
    if (this.user) {
      this.min_date = {year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate()};
      this.configDate.maxDate = undefined;

    }
    //проверяем маску
    if (this.no_time) {
      this.mask = "00.00.0000";
    } else {
      this.mask = "00.00.0000 00:00:00";
    }
  }

  writeValue(newModel: string) {
    if (newModel) {
      this.datetime = Object.assign(this.datetime, DateTimeModel.fromLocalString(newModel));
      this.dateString = newModel;
      this.setDateStringModel();
    } else {
      this.datetime = new DateTimeModel({hour: 0, minute: 0, second: 0});
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputKeyup(e) {
    //передаем дату в родительский элемент для слежения
    this.catchInputDate.emit([e.target.value, this.report_date_type]);
  }

  onInputChange($event: any) {

    const value = $event.target.value;
    const dt = DateTimeModel.fromLocalString(value);

    if (dt) {
      this.datetime = dt;
      this.setDateStringModel();
    } else if (value.trim() === '') {
      this.datetime = new DateTimeModel({hour: 0, minute: 0, second: 0});
      this.dateString = '';
      // //ловим содержимое инпута
      this.catchInputDate.emit([this.dateString, this.report_date_type]);
      this.onChange(this.dateString);
    } else {
      // //ловим содержимое инпута с его номером
      this.catchInputDate.emit([this.dateString, this.report_date_type]);
      this.onChange(value);
    }
  }

  onDateChange($event: any | NgbDateStruct, dp: NgbDatepicker) {
    this.Globals.calendarInterval = false;

    //передаем событие в родительский элемент что дата выбрана пикером
    this.catchPick.emit('picked');

    if ($event != null && $event.year) {
      $event = `${$event.year}/${$event.month}/${$event.day}`
    }

    const date = DateTimeModel.fromLocalString($event);

    if (!date) {
      this.dateString = this.dateString;
      return;
    }

    if (!this.datetime) {
      this.datetime = date;
    }

    this.datetime.year = date.year;
    this.datetime.month = date.month;
    this.datetime.day = date.day;

    dp.navigateTo({year: this.datetime.year, month: this.datetime.month});

    this.setDateStringModel();
  }

  onTimeChange(event: NgbTimeStruct) {

    //передаем событие в родительский элемент что дата выбрана пикером
    this.catchPick.emit('picked');

    this.datetime.hour = event.hour;
    this.datetime.minute = event.minute;
    this.datetime.second = event.second;

    this.setDateStringModel();
  }

  setDateStringModel() {
    this.dateString = this.datetime.toString();
    this.onChange(this.dateString);
  }

  ngOnDestroy() {
    if (Boolean(this.changeLangCancel))
      this.changeLangCancel.unsubscribe();
  }

  checkDT() {
    return (this.datetime.year && this.datetime.month && this.datetime.day);
  }
}
